import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import { caregiverProfileService, SkillDefinition, SkillClaim } from '../services/caregiverProfileService';

// FEAT-080 — Skill selector with gated vs self-attest categories (MVP-18.56).

interface SkillSelectorProps {
  onSaved?: () => void;
}

export default function SkillSelector({ onSaved }: SkillSelectorProps) {
  const [taxonomy, setTaxonomy] = useState<SkillDefinition[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [blocked, setBlocked] = useState<Array<{ skillId: string; label: string; guidance: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [skills, claims] = await Promise.all([
        caregiverProfileService.getSkillTaxonomy(),
        caregiverProfileService.getSkillClaims(),
      ]);
      setTaxonomy(skills);
      setSelected(new Set(claims.map((c) => c.skillId)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleSkill = (skillId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) {
        next.delete(skillId);
      } else {
        next.add(skillId);
      }
      return next;
    });
    setBlocked([]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setBlocked([]);
    try {
      const result = await caregiverProfileService.updateSkillClaims(Array.from(selected));
      if (!result.ok) {
        setBlocked(result.validation.blocked);
        return;
      }
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save skills');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const grouped = taxonomy.reduce<Record<string, SkillDefinition[]>>((acc, skill) => {
    acc[skill.category] = acc[skill.category] ?? [];
    acc[skill.category].push(skill);
    return acc;
  }, {});

  return (
    <Box data-testid="skill-selector">
      <Typography variant="h6" gutterBottom>
        Care Skills
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Select skills you can provide. Advanced skills require an accepted credential, completed
        training module, or caregiver verification (MVP-18.56).
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {blocked.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="skill-blocked-alert">
          {blocked.map((item) => (
            <Typography key={item.skillId} variant="body2">
              <strong>{item.label}:</strong> {item.guidance}
            </Typography>
          ))}
        </Alert>
      ) : null}

      {Object.entries(grouped).map(([category, skills]) => (
        <Box key={category} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {category.replace(/_/g, ' ')}
          </Typography>
          <FormGroup>
            {skills.map((skill) => (
              <FormControlLabel
                key={skill.id}
                data-testid={`skill-checkbox-${skill.id}`}
                control={
                  <Checkbox
                    checked={selected.has(skill.id)}
                    onChange={() => toggleSkill(skill.id)}
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{skill.label}</span>
                    {skill.gated ? <Chip label="Gated" size="small" color="warning" /> : null}
                  </Stack>
                }
              />
            ))}
          </FormGroup>
        </Box>
      ))}

      <Box mt={2}>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={saving}
          data-testid="save-skills-btn"
          sx={{ '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}
        >
          {saving ? 'Saving…' : 'Save skills'}
        </Button>
      </Box>
    </Box>
  );
}
